import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ArrowRight,
  Calendar,
  Check,
  Clock,
  ExternalLink,
  ShieldCheck,
} from 'lucide-react'
import { nilGoLabel } from '@adthlete/common/utils/nilCompliance'
import { DEAL_TYPE_LABELS } from '@/lib/dealTypes'
import { collabPathFor } from '@/lib/collabPath'
import { cn } from '@/lib/utils'
import { useSetCollaborationArchivedMutation, type Collaboration } from '@/redux/marketerApi'
import { useToast } from '@/hooks/use-toast'

type Tone = 'green' | 'blue' | 'amber' | 'red' | 'grey'
const TONE: Record<Tone, string> = {
  green: 'bg-[#E8F9D2] text-[#2F5E06] border-[#C9E9A6]',
  blue: 'bg-[#E0EDFF] text-[#1D4ED8] border-[#BFD7FB]',
  amber: 'bg-[#FFF4E5] text-[#B45309] border-[#FCD9A8]',
  red: 'bg-[#FEECEC] text-[#B91C1C] border-[#FBC4C4]',
  grey: 'bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]',
}

const Pill: React.FC<{ tone: Tone; icon?: React.ReactNode; children: React.ReactNode }> = ({
  tone,
  icon,
  children,
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-left text-xs font-semibold leading-tight [&>svg]:shrink-0',
      TONE[tone],
    )}
  >
    {icon}
    {children}
  </span>
)

const compact = (n: number) =>
  new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n)
const day = (iso: string | null | undefined, year = true) =>
  iso
    ? new Date(String(iso).length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(year ? { year: 'numeric' } : {}),
      })
    : '—'
const daysFrom = (iso: string | null | undefined) =>
  iso
    ? Math.round(
        (new Date(String(iso).length === 10 ? `${iso}T12:00:00` : iso).getTime() - Date.now()) /
          86_400_000,
      )
    : null
const ago = (iso: string) => {
  const d = -(daysFrom(iso) ?? 0)
  return d <= 0 ? 'Today' : d === 1 ? 'Yesterday' : `${d} days ago`
}

/** The athlete: photo, name, sport and school, followers */
export const AthleteCell: React.FC<{ c: Collaboration; size?: 'md' | 'lg' }> = ({
  c,
  size = 'md',
}) => {
  const a = c.athlete
  const name = a?.name ?? 'Athlete'
  return (
    <div className="flex min-w-[170px] max-w-[220px] items-center gap-3">
      {a?.avatar ? (
        <img
          src={a.avatar}
          alt=""
          className={cn(
            'shrink-0 rounded-full object-cover',
            size === 'lg' ? 'h-14 w-14' : 'h-11 w-11',
          )}
        />
      ) : (
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full bg-muted font-semibold text-foreground',
            size === 'lg' ? 'h-14 w-14' : 'h-11 w-11',
          )}
        >
          {name.charAt(0)}
        </span>
      )}
      <div className="min-w-0">
        <p className={cn('truncate font-semibold text-foreground', size === 'lg' && 'text-lg')}>
          {name}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {[a?.sport, a?.school].filter(Boolean).join(' · ') || 'Independent'}
        </p>
        {a?.followers ? (
          <p className="text-xs text-muted-foreground">{compact(a.followers)} followers</p>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Moves a deal to (or back from) this user's Archived tab. Only their own lists change: the
 * athlete, the athlete's manager and teammates still see the deal as before.
 */
const ArchiveButton: React.FC<{ c: Collaboration }> = ({ c }) => {
  const [setArchived, { isLoading }] = useSetCollaborationArchivedMutation()
  const { toast } = useToast()
  const archived = !!c.archived
  const toggle = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await setArchived({ id: c.id, archived: !archived }).unwrap()
      toast({
        title: archived ? 'Deal moved back' : 'Deal archived',
        description: archived
          ? 'It is back in your deal lists.'
          : 'It is in the Archived tab. Only your lists changed.',
      })
    } catch {
      toast({
        title: 'Could not update the deal',
        description: 'Please try again.',
        variant: 'destructive',
      })
    }
  }
  return (
    <button
      type="button"
      onClick={toggle}
      onKeyDown={(e) => e.stopPropagation()}
      disabled={isLoading}
      aria-label={archived ? 'Unarchive deal' : 'Archive deal'}
      title={archived ? 'Unarchive' : 'Archive'}
      className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-border bg-card px-3 text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
    >
      {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
      <span className="hidden sm:inline">{archived ? 'Unarchive' : 'Archive'}</span>
    </button>
  )
}

/** The row's last cell: archive, then open */
const RowActions: React.FC<{ c: Collaboration; label?: string }> = ({ c, label }) => (
  <div className="flex items-center justify-end gap-2">
    <ArchiveButton c={c} />
    <OpenButton to={collabPathFor(c)} label={label} />
  </div>
)

const OpenButton: React.FC<{ to: string; label?: string }> = ({ to, label }) => {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        navigate(to)
      }}
      aria-label={label ?? 'Open'}
      className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-border bg-card px-4 text-sm font-semibold text-foreground hover:bg-muted"
    >
      {label ? (
        <>
          {label} <ArrowRight className="h-4 w-4" />
        </>
      ) : (
        <ArrowRight className="h-4 w-4" />
      )}
    </button>
  )
}

/** What the deal is for: the brand, and the product or what was booked */
const whatLine = (c: Collaboration) => {
  const b = c.booking
  const booked =
    b?.kind === 'rate_card'
      ? `${b.quantity} × ${b.deliverableLabel}`
      : b?.kind === 'custom'
        ? b.description
        : c.dealType && c.dealType !== 'content'
          ? DEAL_TYPE_LABELS[c.dealType]
          : null
  return [c.campaign?.product, booked].filter(Boolean).join(' · ')
}

const complianceCell = (c: Collaboration) => {
  const nil = c.compliance
  if (!nil?.required || nil.state === 'not_required')
    return <span className="text-xs text-muted-foreground">Not required</span>
  if (nil.state === 'cleared')
    return (
      <Pill tone="green" icon={<Check className="h-3.5 w-3.5" />}>
        NIL Go · Cleared
      </Pill>
    )
  if (nil.state === 'overdue')
    return (
      <Pill tone="red" icon={<AlertTriangle className="h-3.5 w-3.5" />}>
        NIL Go · {nilGoLabel(nil)}
      </Pill>
    )
  if (nil.state === 'waiting')
    return (
      <Pill tone="grey" icon={<ShieldCheck className="h-3.5 w-3.5" />}>
        NIL Go · After signing
      </Pill>
    )
  return (
    <Pill tone="amber" icon={<Clock className="h-3.5 w-3.5" />}>
      NIL Go · {nilGoLabel(nil)}
    </Pill>
  )
}

const stepCell = (c: Collaboration) => {
  const step = c.currentMilestone?.name
  if (c.turnState === 'revising')
    return (
      <Pill tone="red" icon={<AlertTriangle className="h-3.5 w-3.5" />}>
        Revising: {step}
      </Pill>
    )
  if (c.actionOwner === 'marketer' || c.hasPendingReview)
    return (
      <Pill tone="amber" icon={<Clock className="h-3.5 w-3.5" />}>
        Review: {step ?? 'Next step'}
      </Pill>
    )
  return (
    <Pill tone="grey" icon={<Clock className="h-3.5 w-3.5" />}>
      Waiting: {step ?? 'Athlete'}
    </Pill>
  )
}

const Progress: React.FC<{ value: number }> = ({ value }) => (
  <div className="flex items-center gap-3">
    <div className="h-2 w-14 overflow-hidden rounded-full bg-[#E5E7EB] 2xl:w-24">
      <div
        className="h-full rounded-full bg-[#2563EB]"
        style={{ width: `${Math.max(4, value)}%` }}
      />
    </div>
    <span className="w-10 shrink-0 text-right text-sm font-semibold tabular-nums text-foreground">
      {value}%
    </span>
  </div>
)

const Table: React.FC<{ head: string[]; children: React.ReactNode; minWidth?: string }> = ({
  head,
  children,
  minWidth = 'min-w-[960px]',
}) => (
  <div className="overflow-x-auto">
    <table className={cn('w-full text-left text-sm', minWidth)}>
      <thead>
        <tr className="border-b border-border text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {head.map((h, i) => (
            <th key={`${h}-${i}`} className="px-3 py-3 font-semibold">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-border">{children}</tbody>
    </table>
  </div>
)

const Row: React.FC<{ to: string; dim?: boolean; children: React.ReactNode }> = ({
  to,
  dim,
  children,
}) => {
  const navigate = useNavigate()
  return (
    <tr
      tabIndex={0}
      onClick={() => navigate(to)}
      onKeyDown={(e) => e.key === 'Enter' && navigate(to)}
      className={cn('cursor-pointer transition-colors hover:bg-[#F9FAF8]', dim && 'opacity-60')}
    >
      {children}
    </tr>
  )
}

const shell = 'rounded-2xl border border-border bg-card shadow-sm'

/** Offers sent: waiting on the athlete to accept, counter or quote */
export const OffersTable: React.FC<{ list: Collaboration[] }> = ({ list }) => (
  <section className={shell}>
    <Table head={['Athlete', 'Campaign', 'Brand / product', 'Status', 'Sent', '']}>
      {list.map((c) => {
        const askingRate = c.booking?.kind === 'custom' && c.booking.askForRate
        return (
          <Row key={c.id} to={collabPathFor(c)}>
            <td className="px-3 py-3">
              <AthleteCell c={c} />
            </td>
            <td className="px-3 py-3 font-semibold text-foreground">
              {c.campaign?.title ?? c.title}
            </td>
            <td className="px-3 py-3">
              <p className="font-semibold text-foreground">{c.campaign?.brand?.name ?? '—'}</p>
              <p className="max-w-[240px] truncate text-xs text-muted-foreground">
                {whatLine(c) || '—'}
              </p>
            </td>
            <td className="px-3 py-3">
              <div className="flex flex-col items-start gap-1.5">
                <Pill tone="blue" icon={<Clock className="h-3.5 w-3.5" />}>
                  {askingRate ? 'Rate requested' : 'Waiting on response'}
                </Pill>
                {c.compliance?.required ? complianceCell(c) : null}
              </div>
            </td>
            <td className="px-3 py-3">
              <p className="text-foreground">{day(c.createdAt)}</p>
              <p className="text-xs text-muted-foreground">{ago(c.createdAt)}</p>
            </td>
            <td className="px-3 py-3 text-right">
              <RowActions c={c} label="Open" />
            </td>
          </Row>
        )
      })}
    </Table>
  </section>
)

/** Active deals: what step each is on, compliance, due date and progress */
export const ActiveTable: React.FC<{ title: string; list: Collaboration[] }> = ({
  title,
  list,
}) => (
  <section className={cn(shell, 'pt-4')}>
    <h2 className="px-4 pb-1 text-sm font-bold uppercase tracking-wider text-muted-foreground">
      {title} · {list.length}
    </h2>
    <Table
      head={['Athlete', 'Campaign', 'Current step', 'Compliance', 'Due date', 'Progress', '']}
      minWidth="min-w-[860px]"
    >
      {list.map((c) => {
        const left = daysFrom(c.contentDueDate)
        return (
          <Row key={c.id} to={collabPathFor(c)}>
            <td className="px-3 py-3">
              <AthleteCell c={c} />
            </td>
            <td className="min-w-[130px] px-3 py-3">
              <p className="font-semibold text-foreground">{c.campaign?.title ?? c.title}</p>
              <p className="text-xs text-muted-foreground">{c.campaign?.brand?.name ?? ''}</p>
            </td>
            <td className="px-3 py-3">{stepCell(c)}</td>
            <td className="px-3 py-3">{complianceCell(c)}</td>
            <td className="whitespace-nowrap px-3 py-3">
              <p className="flex items-center gap-1.5 text-foreground">
                <Calendar className="h-4 w-4 text-muted-foreground" /> {day(c.contentDueDate)}
              </p>
              {left !== null ? (
                <p
                  className={cn(
                    'text-xs',
                    left < 0
                      ? 'font-semibold text-[#DC2626]'
                      : left <= 3
                        ? 'text-[#DC2626]'
                        : 'text-muted-foreground',
                  )}
                >
                  {left < 0
                    ? `${-left} days overdue`
                    : left === 0
                      ? 'Due today'
                      : `${left} day${left === 1 ? '' : 's'} left`}
                </p>
              ) : null}
            </td>
            <td className="px-3 py-3">
              <Progress value={c.progress ?? 0} />
            </td>
            <td className="px-3 py-3 text-right">
              <RowActions c={c} />
            </td>
          </Row>
        )
      })}
    </Table>
  </section>
)

/** Completed deals: who, which campaign, cleared, when it finished */
export const CompletedList: React.FC<{ list: Collaboration[] }> = ({ list }) => {
  const navigate = useNavigate()
  return (
    <ul className="space-y-3">
      {list.map((c) => (
        <li
          key={c.id}
          tabIndex={0}
          onClick={() => navigate(collabPathFor(c))}
          onKeyDown={(e) => e.key === 'Enter' && navigate(collabPathFor(c))}
          className={cn(
            shell,
            'grid cursor-pointer gap-4 p-4 transition-colors hover:bg-[#F9FAF8] md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_190px_minmax(0,1fr)_auto] md:items-center',
          )}
        >
          <AthleteCell c={c} size="lg" />
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground">
              {c.campaign?.title ?? c.title}
            </p>
            {c.campaign?.brand?.name ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  if (c.campaignId) navigate(`/campaign/${c.campaignId}`)
                }}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                {c.campaign.brand.name} <ExternalLink className="h-3.5 w-3.5" />
              </button>
            ) : null}
            {c.dealType ? (
              <span className="mt-1 block w-fit rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-foreground">
                {DEAL_TYPE_LABELS[c.dealType]}
              </span>
            ) : null}
          </div>
          <div className="flex flex-col items-start gap-1.5">
            <Pill tone="green" icon={<Check className="h-3.5 w-3.5" />}>
              Completed
            </Pill>
            {c.compliance?.required ? complianceCell(c) : null}
          </div>
          <div>
            <p className="flex items-center gap-1.5 text-sm text-foreground">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              Completed {day(c.completedAt ?? c.updatedAt)}
            </p>
            <div className="mt-2">
              <Progress value={100} />
            </div>
          </div>
          <RowActions c={c} />
        </li>
      ))}
    </ul>
  )
}

/** Deals this user archived, most recently archived first, each with Unarchive */
export const ArchivedTable: React.FC<{ list: Collaboration[] }> = ({ list }) => (
  <section className={shell}>
    <Table head={['Athlete', 'Campaign', 'Status', 'Archived', '']} minWidth="min-w-[720px]">
      {list.map((c) => (
        <Row key={c.id} to={collabPathFor(c)}>
          <td className="px-3 py-3">
            <AthleteCell c={c} />
          </td>
          <td className="px-3 py-3">
            <p className="font-semibold text-foreground">{c.campaign?.title ?? c.title}</p>
            <p className="text-xs text-muted-foreground">{c.campaign?.brand?.name ?? ''}</p>
          </td>
          <td className="px-3 py-3">{statusCell(c)}</td>
          <td className="whitespace-nowrap px-3 py-3 text-foreground">{day(c.archivedAt)}</td>
          <td className="px-3 py-3 text-right">
            <RowActions c={c} />
          </td>
        </Row>
      ))}
    </Table>
  </section>
)

const statusCell = (c: Collaboration) => {
  if (c.status === 'offered')
    return (
      <Pill tone="blue" icon={<Clock className="h-3.5 w-3.5" />}>
        Offer sent
      </Pill>
    )
  if (c.status === 'pending_brand')
    return (
      <Pill tone="amber" icon={<Clock className="h-3.5 w-3.5" />}>
        Your decision
      </Pill>
    )
  if (c.status === 'rejected') return <Pill tone="grey">Declined</Pill>
  if (c.status === 'expired') return <Pill tone="grey">Expired</Pill>
  if (c.status === 'withdrawn') return <Pill tone="grey">Withdrawn</Pill>
  if (c.isComplete || c.status === 'completed')
    return (
      <Pill tone="green" icon={<Check className="h-3.5 w-3.5" />}>
        Completed
      </Pill>
    )
  return stepCell(c)
}

/** Every athlete on one campaign, whatever stage their deal is at */
export const CampaignAthletesTable: React.FC<{ list: Collaboration[] }> = ({ list }) => (
  <section className={shell}>
    <Table
      head={['Athlete', 'Status', 'Compliance', 'Due date', 'Progress', '']}
      minWidth="min-w-[760px]"
    >
      {/* Deals this user archived go last */}
      {[...list.filter((c) => !c.archived), ...list.filter((c) => c.archived)].map((c) => {
        const left = c.isComplete ? null : daysFrom(c.contentDueDate)
        return (
          <Row key={c.id} to={collabPathFor(c)} dim={!!c.archived}>
            <td className="px-3 py-3">
              <AthleteCell c={c} />
            </td>
            <td className="px-3 py-3">
              <div className="flex flex-col items-start gap-1.5">
                {statusCell(c)}
                {c.archived ? <Pill tone="grey">Archived</Pill> : null}
              </div>
            </td>
            <td className="px-3 py-3">{complianceCell(c)}</td>
            <td className="whitespace-nowrap px-3 py-3">
              <p className="flex items-center gap-1.5 text-foreground">
                <Calendar className="h-4 w-4 text-muted-foreground" /> {day(c.contentDueDate)}
              </p>
              {left !== null && c.status === 'accepted' ? (
                <p
                  className={cn(
                    'text-xs',
                    left < 0
                      ? 'font-semibold text-[#DC2626]'
                      : left <= 3
                        ? 'text-[#DC2626]'
                        : 'text-muted-foreground',
                  )}
                >
                  {left < 0
                    ? `${-left} days overdue`
                    : left === 0
                      ? 'Due today'
                      : `${left} day${left === 1 ? '' : 's'} left`}
                </p>
              ) : null}
            </td>
            <td className="px-3 py-3">
              <Progress value={c.status === 'accepted' ? (c.progress ?? 0) : 0} />
            </td>
            <td className="px-3 py-3 text-right">
              <RowActions c={c} />
            </td>
          </Row>
        )
      })}
    </Table>
  </section>
)

const usd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
const dollars = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)

/** Money on one campaign: what's committed to accepted deals, paid, still owed, and out in offers */
export const CampaignPaymentsPanel: React.FC<{
  list: Collaboration[]
  budget?: number | null
}> = ({ list, budget }) => {
  const amount = (c: Collaboration) => Number(c.paymentAmount ?? 0)
  const signed = list.filter((c) => c.status === 'accepted')
  const offers = list.filter((c) => c.status === 'offered' || c.status === 'pending_brand')
  const committed = signed.reduce((s, c) => s + amount(c), 0)
  const paid = signed.filter((c) => c.paymentStatus === 'paid').reduce((s, c) => s + amount(c), 0)
  const inOffers = offers.reduce((s, c) => s + amount(c), 0)
  const cap = budget && budget > 0 ? budget : null
  const pct = (n: number) =>
    cap ? Math.min(100, (n / cap) * 100) : committed ? (n / committed) * 100 : 0
  const rows = [...signed, ...offers]

  const tiles = [
    {
      label: 'Budget',
      value: cap ? dollars(cap) : '—',
      note: cap ? `${dollars(Math.max(0, cap - committed))} left` : 'Not set',
    },
    {
      label: 'Committed',
      value: dollars(committed),
      note: `${signed.length} accepted deal${signed.length === 1 ? '' : 's'}`,
    },
    { label: 'Paid', value: dollars(paid), note: 'Released to athletes', tone: 'text-[#2F5E06]' },
    {
      label: 'Still owed',
      value: dollars(committed - paid),
      note: 'On accepted deals',
      tone: committed - paid > 0 ? 'text-[#B45309]' : '',
    },
    {
      label: 'In open offers',
      value: dollars(inOffers),
      note: `${offers.length} waiting on a reply`,
    },
  ]

  return (
    <div className="space-y-4">
      <section className={cn(shell, 'space-y-5 p-4 sm:p-5')}>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {tiles.map((t) => (
            <div key={t.label} className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t.label}
              </p>
              <p className={cn('mt-1 text-2xl font-bold tabular-nums text-foreground', t.tone)}>
                {t.value}
              </p>
              <p className="text-xs text-muted-foreground">{t.note}</p>
            </div>
          ))}
        </div>
        <div>
          <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-[#EEF1EA]">
            <div className="bg-[#65A30D]" style={{ width: `${pct(paid)}%` }} />
            <div className="bg-[#C0FD72]" style={{ width: `${pct(committed - paid)}%` }} />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[#65A30D]" /> Paid
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[#C0FD72]" /> Owed
            </span>
            {cap ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-[#EEF1EA] ring-1 ring-border" /> Budget
                left
              </span>
            ) : null}
          </div>
        </div>
      </section>

      {rows.length ? (
        <section className={shell}>
          <Table head={['Athlete', 'Deal', 'Amount', 'Payment', '']} minWidth="min-w-[720px]">
            {rows.map((c) => (
              <Row key={c.id} to={collabPathFor(c)}>
                <td className="px-3 py-3">
                  <AthleteCell c={c} />
                </td>
                <td className="px-3 py-3 text-foreground">
                  {DEAL_TYPE_LABELS[c.dealType ?? 'content'] ?? 'Content'}
                </td>
                <td className="px-3 py-3 font-semibold tabular-nums text-foreground">
                  {c.paymentAmount ? usd(amount(c)) : 'TBD'}
                </td>
                <td className="px-3 py-3">
                  {c.status !== 'accepted' ? (
                    <Pill tone="grey">Offer pending</Pill>
                  ) : c.paymentStatus === 'paid' ? (
                    <Pill tone="green" icon={<Check className="h-3.5 w-3.5" />}>
                      Paid
                    </Pill>
                  ) : (
                    <Pill tone="amber" icon={<Clock className="h-3.5 w-3.5" />}>
                      Owed
                    </Pill>
                  )}
                </td>
                <td className="px-3 py-3 text-right">
                  <OpenButton to={collabPathFor(c)} />
                </td>
              </Row>
            ))}
          </Table>
        </section>
      ) : (
        <section className={cn(shell, 'p-8 text-center text-sm text-muted-foreground')}>
          No deals on this campaign yet. Payments show here once an athlete accepts.
        </section>
      )}
    </div>
  )
}
